import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import {
  fetchLeadScore,
  rescoreLead,
  scoreKey,
  type LeadScore,
  type LeadScoreResponse,
  type ModelStatus,
  type ScoreClass,
  type ScoreFactor,
  type TextReading,
} from './leadScoreApi'

type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * Оценка лида на карточке (#129): горячий / тёплый / холодный и почему.
 *
 * ## Что говорится словами, а не цветом
 *
 * - **Класс** — плашкой со словом («Горячий»), не только цветом.
 * - **Чем посчитано**: модель даёт вероятность конверсии; правило — баллы и пометку «правило, не
 *   модель». Процента у правила нет, и экран его не рисует: пустое место читалось бы как ноль.
 * - **Текст заявки**: что увидел ИИ (намерение, срочность, бюджет, спам) или почему текст не
 *   разобран — «ИИ выключен» и «текст прочитан, ничего не нашёл» это разные факты.
 * - **Факторы** — фразами с направлением («Корпоративная почта — повышает»): вклад в логарифме
 *   шансов человеку ничего не говорит.
 *
 * Оценки ещё нет — так и сказано: у нового лида она появляется через секунды после приёма.
 */
export function LeadScorePanel({ leadId }: { leadId: string }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: scoreKey(leadId),
    queryFn: ({ signal }) => fetchLeadScore(leadId, signal),
    retry: false,
  })
  const rescore = useMutation({
    mutationFn: () => rescoreLead(leadId),
    onSuccess: (data) => queryClient.setQueryData(scoreKey(leadId), data),
  })
  const mayRescore = can(user, 'sales.lead', 'update')

  if (query.isLoading) {
    return <p className="t-sm muted">{t('leadScore.loading')}</p>
  }
  if (query.error) {
    return <p className="t-sm muted" role="alert">{t('leadScore.error')}</p>
  }
  const response = query.data as LeadScoreResponse | undefined
  const score = response?.score
  return (
    <div className="stack" style={{ gap: 8 }} data-testid="lead-score">
      {score?.scoreClass == null ? (
        <p className="t-sm muted" data-testid="lead-score-none">{t('leadScore.none')}</p>
      ) : (
        <ScoreBody score={score} modelStatus={response?.modelStatus} t={t} />
      )}
      {rescore.error != null && (
        <p className="t-sm" role="alert">
          {rescore.error instanceof ApiError ? rescore.error.message : String(rescore.error)}
        </p>
      )}
      {mayRescore && (
        <div>
          <button type="button" className="btn btn-ghost btn-sm" disabled={rescore.isPending}
                  onClick={() => rescore.mutate()}>
            {rescore.isPending ? t('leadScore.rescoring') : t('leadScore.rescore')}
          </button>
        </div>
      )}
    </div>
  )
}

const TONE: Record<ScoreClass, string> = { hot: 'b-danger', warm: 'b-warning', cold: 'b-info' }

function ScoreBody({ score, modelStatus, t }: { score: LeadScore; modelStatus: ModelStatus | undefined; t: Translate }) {
  const scoreClass = score.scoreClass as ScoreClass
  return (
    <>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={`badge ${TONE[scoreClass] ?? ''}`} data-testid="lead-score-class">
          {t(`value.sales.lead.score_class.${scoreClass}`)}
        </span>
        <span className="t-sm" data-testid="lead-score-method">{methodText(t, score, modelStatus)}</span>
      </div>
      {score.text != null && <p className="t-sm" data-testid="lead-score-text">{textLine(t, score.text)}</p>}
      {(score.factors ?? []).length > 0 && (
        <ul className="forecast-factors" aria-label={t('leadScore.why')}>
          {(score.factors ?? []).map((factor) => (
            <li key={`${factor.kind}-${factor.code}`} data-effect={factor.contribution >= 0 ? 'up' : 'down'}>
              {factorText(t, factor)}
            </li>
          ))}
        </ul>
      )}
      {(score.notes ?? []).map((note) => (
        <p key={note} className="t-xs muted">{note}</p>
      ))}
    </>
  )
}

/** «Вероятность конверсии 72 % — модель» или «Правило, не модель: 6 баллов». */
export function methodText(t: Translate, score: LeadScore, modelStatus: ModelStatus | undefined): string {
  if (score.method === 'model' && score.probability != null) {
    return t('leadScore.byModel', { probability: `${Math.round(score.probability * 100)} %` })
  }
  return t(modelStatus === 'insufficient_data' ? 'leadScore.byRuleInsufficient' : 'leadScore.byRule',
    { points: score.points ?? 0 })
}

export function textLine(t: Translate, text: TextReading): string {
  if (text.status !== 'analyzed') {
    return t(`leadScore.text.${text.status}`)
  }
  const parts = [t(`leadScore.intent.${text.intent ?? 'none'}`)]
  if (text.urgent) parts.push(t('leadScore.signal.urgent'))
  if (text.budgetMentioned) parts.push(t('leadScore.signal.budget'))
  if (text.spam) parts.push(t('leadScore.signal.spam'))
  const reason = text.reason ? ` — «${text.reason}»` : ''
  return t('leadScore.text.analyzed', { signals: parts.join(', ') }) + reason
}

/** Фраза фактора: признак со значением и направление. */
export function factorText(t: Translate, factor: ScoreFactor): string {
  const value = factor.value == null ? '' : typeof factor.value === 'boolean'
    ? t(factor.value ? 'leadScore.value.yes' : 'leadScore.value.no')
    : factor.code === 'source' ? t(`value.sales.lead.source.${String(factor.value)}`)
      : factor.code === 'intent' ? t(`leadScore.intent.${String(factor.value)}`)
        : String(factor.value)
  return t('leadScore.factorLine', {
    factor: t(`leadScore.factor.${factor.code}`, { value }),
    effect: t(factor.contribution >= 0 ? 'forecast.effect.up' : 'forecast.effect.down'),
    kind: t(`leadScore.kind.${factor.kind}`),
  })
}
